import { useState, FC } from 'react';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import { LoadingOverlay } from '@cbridge-tools/overlay';
import SimpleBar from 'simplebar-react';
import { getDrawerSxTransitionMixin, getDrawerWidthTransitionMixin } from '@cbridge-tools/navigation-item';
import Drawer from '@mui/material/Drawer';
import { useDrawerWidthTransitionTimeout } from './_hooks/useDrawerWidthTransitionTimeout';
import { useSidebarSettings } from './_hooks/useSidebarSettings';
import { DashboardSidebarSubNavigation } from './_components/DashboardSidebarSubNavigation/DashboardSidebarSubNavigation';
import { ResizeHandleBar } from './_components/ResizeHandleBar/ResizeHandleBar';
import { ToggleMenuButton } from './_components/ToggleMenuButton/ToggleMenuButton';
import { SidebarFilters } from './_components/SidebarFilters/SidebarFilters';
import { DRAWER_EXPANDED_WIDTH_DEFAULT, DRAWER_COLLAPSED_WIDTH } from './SidebarNavigationDrawer.constant';
import { ISidebarNavigationDrawerProps } from './SidebarNavigationDrawer.interface';

export const SidebarNavigationDrawer: FC<ISidebarNavigationDrawerProps> = (props) => {
  const {
    navigation = [],
    navigationBasePath,
    navigationVariant,
    isNavigationLoading,
    isCollapsible = false,
    isResizable = false,
    defaultSidebarCollapsed = false,
    sidebarExpandedWidth = DRAWER_EXPANDED_WIDTH_DEFAULT,
    settingsKey,
    slots,
    slotProps,
    elevation = 1,
    ref = null,
  } = props;

  const theme = useTheme();

  // Saved values seed the state once at mount; later changes are written through (LB-4992).
  const { savedCollapsed, savedWidth, saveCollapsed, saveWidth } = useSidebarSettings(settingsKey);
  const [isNavigationExpanded, setIsNavigationExpanded] = useState(!(savedCollapsed ?? defaultSidebarCollapsed));
  const [currentWidth, setCurrentWidth] = useState<number>(savedWidth ?? sidebarExpandedWidth);
  const [isResizing, setIsResizing] = useState(false);

  const isUnderMdViewport = useMediaQuery(
    theme.breakpoints.down('md'),
  );

  const isNavigationFullyExpanded = useDrawerWidthTransitionTimeout(isNavigationExpanded);

  const toggleNavigationExpanded = () => {
    const expanded = !isNavigationExpanded;
    setIsNavigationExpanded(expanded);
    saveCollapsed(!expanded);
  };

  const isMini = isCollapsible && (!isNavigationExpanded || isUnderMdViewport);

  const SidebarHeaderSlot = slots?.sidebarHeader ?? null;
  const SidebarFooterSlot = slots?.sidebarFooter ?? null;

  const getDrawerSharedSx = () => {
    // Use currentWidth only when expanded and not in mini mode
    const drawerWidth = isMini
      ? DRAWER_COLLAPSED_WIDTH
      : currentWidth;

    // Only apply transitions when not resizing
    const transitionStyles = !isResizing
      ? getDrawerWidthTransitionMixin(isNavigationExpanded)
      : { overflowX: 'hidden' };

    return {
      displayPrint: 'none',
      width: drawerWidth,
      flexShrink: 0,
      ...transitionStyles,
      '& .MuiDrawer-paper': {
        position: 'absolute',
        width: drawerWidth,
        boxSizing: 'border-box',
        backgroundImage: 'none',
        ...transitionStyles,
      },
    };
  };

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Drawer
        variant={'permanent'}
        slotProps={{
          paper: {
            elevation,
            sx: { overflow: 'hidden', display: 'flex', flexDirection: 'column' },
          },
        }}
        sx={{
          display: 'block',
          ...getDrawerSharedSx(),
        }}
      >
        <Toolbar />
        {SidebarHeaderSlot
          ? (
            <Box>
              <SidebarHeaderSlot mini={isMini} isFullyExpanded={isNavigationFullyExpanded} {...slotProps?.sidebarHeader} />
            </Box>
          )
          : null}
        <Box
          component={'nav'}
          aria-label={`Navigation-${navigationVariant ?? 'primary'}`}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...(isCollapsible
              ? getDrawerSxTransitionMixin(isNavigationFullyExpanded, 'padding')
              : {}),
          }}
        >
          <Box ref={ref} sx={{ overflow: 'hidden', flex: 1 }}>
            <SimpleBar style={{ height: '100%', width: '100%', overflow: 'auto' }}>
              {isNavigationLoading
                ? <LoadingOverlay />
                : (
                  <>
                    <DashboardSidebarSubNavigation
                      items={navigation}
                      variant={navigationVariant}
                      basePath={navigationBasePath}
                      isMini={isMini}
                      isFullyExpanded={isNavigationFullyExpanded}
                      hasDrawerTransitions={isCollapsible}
                    />
                    <SidebarFilters filters={slots?.sidebarFilters} mini={isMini} isFullyExpanded={isNavigationFullyExpanded} />
                  </>
                )}
            </SimpleBar>
          </Box>
          {SidebarFooterSlot
            ? (
              <Box sx={{
                marginTop: 'auto',
              }}>
                <SidebarFooterSlot
                  mini={isMini}
                  isFullyExpanded={isNavigationFullyExpanded}
                  toggleAction={(
                    <Box
                      sx={{
                        display: (isCollapsible && !isUnderMdViewport)
                          ? 'block'
                          : 'none',
                      }}
                    >
                      <ToggleMenuButton isExpanded={isNavigationExpanded} toggleExpanded={toggleNavigationExpanded} />
                    </Box>
                  )}
                  {...slotProps?.sidebarFooter}
                />
              </Box>
            )
            : null}
        </Box>
      </Drawer>
      {isResizable && !isMini && (
        <ResizeHandleBar
          width={currentWidth}
          updateWidth={setCurrentWidth}
          isResizing={isResizing}
          setIsResizing={setIsResizing}
          onResizeEnd={saveWidth}
        />
      )}
    </Box>
  );
};
